import React, {useEffect, useState, useCallback} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import SubHeader from '../../../components/SubHeader';
import ConfirmModal from '@/components/ConfirmModal';
import {useLoadingStore} from "@/stores/loading";
import {deleteUser, getUsers, resetPassword} from "@/api/users";
import {MESSAGE_NO_E37, MESSAGE_NO_E39, PERMISSION_ROLL} from "@/config";
import * as Common from "@/utils/common";
import {useUserStore, UserListHeader} from "@/stores/user";
import * as Config from "@/config";
import {User, LastUpdated} from "@/containers/admin/Users/types";
import {useForm} from "react-hook-form";
import { Project, ProjectCategory, useProjectStore } from "@/stores/project";
import qs from 'qs';
import {SortIndicator, SortParams} from "@/components/SortIndicator";

interface searchUser {
  group_id: number,
  roll: string,
  last_name: string,
  first_name: string,
  project_id: number,
}

const initialState = {
  groupId: "",
  roll: "",
  lastName: "",
  firstName: "",
  projectId: "",
};

interface Group {
  id: number,
  name: string,
}

export default function UserList() {
  const navigate = useNavigate();
  const setLoading = useLoadingStore(state => state.setLoading);
  const user = useUserStore(state => state.user);
  const canUser = Common.canUserManagement(user)

  const [isShowPWModal, setIsShowPWModal] = useState(false);
  const [resetPWId, setResetPWId] = useState<number>(0);
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<LastUpdated>();
  const [isShowErrorModal, setIsShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 検索項目
  const [groupId, setGroupId] = useState<string>(initialState.groupId);
  const [roll, setRoll] = useState<string>(initialState.roll);
  const [lastName, setLastName] = useState<string>(initialState.lastName);
  const [firstName, setFirstName] = useState<string>(initialState.firstName);
  const [projectId, setProjectId] = useState<string>(initialState.projectId);
  const [projects, setProjects] = useState<Project[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [userQueryParams, setUserQueryParams, setDoSort, sortParams, setSortParams] = useUserStore(state => [
    state.queryParams,
    state.setQueryParams,
    state.setDoSort,
    state.sortParams,
    state.setSortParams
  ]);
  const [searchQueryParam, setSearchQueryParam] = useState<any>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: {errors}
  } = useForm<any>(
    {
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
      criteriaMode: 'all',
    }
  );
  const [
    selectedProject,
    selectedAdminCategory,
  ] = useProjectStore(state => [
    state.project,
    state.adminCategory,
  ]);

  const breadCrumbs = [
    { title: selectedProject?.name },
    { title: selectedAdminCategory?.name },
    {title: 'ユーザ一覧'},
  ];

  const onClickNew = () => {
    navigate("/admin/users/new");
  }
  const onClickDuplicate = () => {
    if (selectedUser) {
      navigate(`/admin/users/${selectedUser}/duplicate`);
    }
  }

  const openDeleteUserModal = (id: number) => {
    setIsShowDeleteModal(true);
    setDeleteId(id);
  }

  const submitDeleteUser = () => {
    setLoading(true);
    const response = deleteUser(deleteId);
    response.then(result => {
      setUsers(result.data.users);
      setLastUpdated(result.data.last_updated)
      setIsShowDeleteModal(false);
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E37);
    }).finally(() => {
      setLoading(false);
    });
  }

  const openResetPWModal = (id: number) => {
    setIsShowPWModal(true);
    setResetPWId(id);
  }

  const submitResetPassword = () => {
    setLoading(true);
    const response = resetPassword(resetPWId);
    response.then(result => {
      setUsers(result.data.users);
      setLastUpdated(result.data.last_updated)
      setIsShowPWModal(false);
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E37);
    }).finally(() => {
      setLoading(false);
    });
  }

  const findLabelByValue = (value: string) => {
    const roll = PERMISSION_ROLL.find((item) => item.value === value);
    return roll ? roll.label : '未定義';
  };


  useEffect(() => {
    // URLパラメータから検索条件を取得し、状態に反映する
    const params = qs.parse(searchParams.toString(), {ignoreQueryPrefix: true});
    if (typeof params.group_id === 'string') {
      setGroupId(params.group_id);
    }
    if (typeof params.roll === 'string') {
      setRoll(params.roll);
    }
    if (typeof params.last_name === 'string') {
      setLastName(params.last_name);
    }
    if (typeof params.first_name === 'string') {
      setFirstName(params.first_name);
    }
    if (typeof params.project_id === 'string') {
      setProjectId(params.project_id);
    }
  }, [searchParams]);

  useEffect(() => {
    if (userQueryParams) {
      setSearchQueryParam(userQueryParams);
    }
    doSearch(userQueryParams, 1);
  }, []);

  useEffect(() => {
    if (errorMessage) {
      setIsShowErrorModal(true);
    }
  }, [errorMessage])

  useEffect(() => {
    if (canUser < Config.RESTRICTION_HISTORY_USER_OK.value) {
      navigate('/admin');
    }
  }, [])

  // 検索処理
  function doSearch(params: any, page = 1) {
    setLoading(true);
    const postParams: { [key: string]: any; } = {
      'params': params,
    };

    //console.log(postParams);
    const response = getUsers(postParams);
    response.then(result => {
      setUsers(result.data.users);
      setLastUpdated(result.data.last_updated);
      setProjects(result.data.projects);
      setGroups(result.data.groups);

      setSearchQueryParam(result.data.params);
      setSearchParams(qs.stringify(params));
      setUserQueryParams(params);
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E39);
    }).finally(() => {
      setLoading(false);
    });
  }

  const onSearch = () => {
    const params = {
      group_id: groupId,
      roll: roll,
      last_name: lastName,
      first_name: firstName,
      project_id: projectId,
    }
    doSearch(params, 1);
  }

  const onSort = useCallback((sortParam: SortParams) => {
    if (sortParam.item_name) {
      doSearch({
        ...userQueryParams,
        sort_column_name: sortParam.item_name,
        sort_order: sortParam.order
      });
    }
  }, [userQueryParams]);

  const onClear = () => {
    setGroupId(initialState.groupId);
    setRoll(initialState.roll);
    setLastName(initialState.lastName);
    setFirstName(initialState.firstName);
    setProjectId(initialState.projectId);
    setSortParams({} as SortParams);
  }

  useEffect(() => {
    setDoSort(onSort);
    return () => {
      setDoSort(() => {
      });
    };
  }, [onSort]);

  return (
    <div>
      <div className="sub-header">
        <SubHeader breadCrumbs={breadCrumbs} title="ユーザ管理"/>
        <div
          className="uk-text-right uk-margin-small-bottom">最終更新ユーザ：{lastUpdated?.last_updated_by} 最終更新:{lastUpdated?.last_updated_at}</div>

        <form>
          <table className="condition-tbl uk-margin-small-bottom">
            <tbody>
            <tr className="header">
              <th>グループ</th>
              <th>権限</th>
              <th>姓</th>
              <th>名</th>
              <th>プロジェクト</th>
            </tr>
            <tr>
              <td>
                <div>
                  <select
                    className={`${groupId ? '' : 'placeholder'}`}
                    value={groupId || ""}
                    name="group_id"
                    onChange={(e) => {
                      setGroupId(e.target.value);
                    }}
                  >
                    <option value="">グループを選択</option>
                  {groups?.map(g => (
                    <option key={`groups_${g.id}`} value={g.id}>{g.name}</option>
                  ))}
                  </select>
                </div>
              </td>
              <td>
                <div>
                  <select
                    className={`${roll ? '' : 'placeholder'}`}
                    defaultValue={""}
                    value={roll}
                    name="roll"
                    onChange={(e) => {
                      setRoll(e.target.value);
                    }}
                  >
                    <option value="">権限選択</option>
                    {PERMISSION_ROLL?.map((item, index) => (
                      <option key={index} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </td>
              <td>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </td>
              <td>
                <div>
                  <select
                    className={`${projectId ? '' : 'placeholder'}`}
                    value={projectId || ""}
                    name="project_id"
                    onChange={(e) => {
                      setProjectId(e.target.value);
                    }}
                  >
                    <option value="">プロジェクトを選択</option>
                  {projects?.map(p => (
                    <option key={`projects_${p.id}`} value={p.id}>{p.name}</option>
                  ))}
                  </select>
                </div>
              </td>
            </tr>

            <tr>
              <td colSpan={12}>
                <div className="uk-flex uk-flex-center">
                  <button
                    type="button"
                    className="uk-button--m uk-button-refer"
                    onClick={() => onSearch()}>検索
                  </button>
                  <button
                    type="button"
                    className="uk-button--m uk-button-cancel uk-margin-left"
                    onClick={() => onClear()}>クリア
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </form>

        <div className="uk-flex-right uk-flex uk-flex-middle">
          <button className="uk-button--m uk-button-refer uk-margin-left" onClick={onClickDuplicate}>複製</button>
          <button className="uk-button--m uk-button-refer uk-margin-left" onClick={onClickNew}>新規作成</button>
        </div>
      </div>
      <div className="container">
        <div className="user-tbl-wrap">
          <table className="user-tbl">
            <thead>
            <tr>
              <th>
                <div className="border-box"/>
              </th>
              <th className="uk-text-center">
                <div className="border-box"/>
                No.
              </th>
              {UserListHeader.map((column, index) =>
                <th key={`list-header-${index}`} className="sort-indicator-wrap">
                  <div className="border-box"/>
                  {column.headerName}
                  {column.sortItemName != '' &&
                      <SortIndicator item_name={column.sortItemName} isUser={true}/>
                  }
                </th>
              )}
              <th className="uk-text-center">
                <div className="border-box"/>
                PW初期化
              </th>
              <th className="uk-text-center">
                <div className="border-box"/>
                削除
              </th>
            </tr>
            </thead>
            <tbody>
            {users?.map((item, index) => (
              <tr
                className={item.expired ? "" : ""}
                style={{backgroundColor: item.expired ? 'darkgray' : ''}}
                key={index}
              >
                <td className="uk-text-center">
                  <div className="border-box"/>
                  <input type="radio"
                         name="selectRow"
                         value={item.id}
                         onChange={(e) => setSelectedUser(Number(e.target.value))}
                  />
                </td>
                <td className="uk-text-center">
                  <div className="border-box"/>
                  {index + 1}</td>
                <td className="uk-text-center">
                  <div className="border-box"/>
                  <Link to={`/admin/users/${item.id}/edit`}>{item.id}</Link>
                </td>
                <td>
                  <div className="border-box"/>
                  {item.group_name}</td>
                <td>
                  <div className="border-box"/>
                  {findLabelByValue(item.roll)}</td>
                <td>
                  <div className="border-box"/>
                  {item.email_address}</td>
                <td>
                  <div className="border-box"/>
                  {item.last_name}</td>
                <td>
                  <div className="border-box"/>
                  {item.first_name}</td>
                <td>
                  <div className="border-box"/>
                  {item.projects.map((project, index) => (
                    <React.Fragment key={index}>
                      {index !== 0 && <br/>}
                      {project.name}
                    </React.Fragment>
                  ))}
                </td>
                <td>
                  <div className="border-box"/>
                  {item.created}</td>
                <td>
                  <div className="border-box"/>
                  {item.expiration_date}</td>
                <td className="uk-text-center">
                  <div className="border-box"/>
                  <a
                    onClick={() => {
                      if (item.is_changed_password !== 0) {
                        openResetPWModal(item.id);
                      }
                    }}
                    style={{
                      color: item.is_changed_password === 0 ? 'gray' : '',
                      cursor: item.is_changed_password === 0 ? 'not-allowed' : '',
                      textDecoration: item.is_changed_password === 0 ? 'none' : ''
                    }}
                  >
                    PW初期化
                  </a>
                </td>
                <td className="uk-text-center">
                  <div className="border-box"/>
                  <a
                    onClick={() => {
                      openDeleteUserModal(item.id)
                    }}>削除
                  </a>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmModal
        text="ユーザのパスワードを初期化しますか？"
        confirmButtonText="初期化"
        isShow={isShowPWModal}
        onConfirm={() => {
          submitResetPassword();
        }}
        onCancel={() => {
          setIsShowPWModal(false);
        }}
      />
      <ConfirmModal
        text="データを削除しますか？"
        confirmButtonText="削除"
        isShow={isShowDeleteModal}
        onConfirm={() => {
          submitDeleteUser();
        }}
        onCancel={() => {
          setIsShowDeleteModal(false);
        }}
      />
      {errorMessage &&
          <ConfirmModal
              text={errorMessage}
              confirmButtonText="OK"
              isShow={isShowErrorModal}
              onConfirm={() => {
                setIsShowErrorModal(false);
                setErrorMessage(null);
              }}
          />
      }
    </div>
  );
}